import { useEffect } from 'react';

/**
 * Ctrl/⌘ + Shift + O — marca en rojo lo que se sale de su recuadro.
 *
 * Nace de una pregunta que no supe contestar sin mentir: «¿ya no hay textos
 * que se salgan?». Se puede auditar el código y encontrar los sospechosos de
 * siempre (`nowrap` sin salida, `truncate` en un flex sin `min-w-0`), pero el
 * desborde depende del ancho real, de la fuente y del texto que haya en la
 * base de datos ese día. Un nombre de cliente largo rompe una tarjeta que con
 * los datos de prueba se veía perfecta.
 *
 * Y hay una razón extra desde que la app está en español: casi todo el texto
 * creció al traducirlo —«Save» mide cuatro letras y «Guardar cambios» quince—
 * así que un botón que cabía justo ya no cabe, y eso no se ve en el diff.
 *
 * Solo en desarrollo. Marca dos cosas distintas:
 *
 *   rojo    el contenido es más ancho que la caja y nadie lo recorta: pisa al
 *           vecino o se corta a media palabra.
 *   naranja la caja se sale de la pantalla y arrastra el scroll horizontal.
 *
 * Lo que scrollea a propósito (una tabla dentro de su `TableWrap`) no se marca:
 * ahí desbordar es la función, no el fallo.
 */

const ATAJO = 'KeyO';
const ID_ESTILO = 'rp-detector-desborde';

interface Hallazgo {
    texto: string;
    etiqueta: string;
    clases: string;
    exceso: number;
    ancho: number;
    motivo: 'se sale de su caja' | 'se sale de la pantalla';
}

/** ¿Vive dentro de algo que ya scrollea o recorta? Entonces no se ve mal. */
function estaContenido(el: Element): boolean {
    for (let p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) {
        if (/auto|scroll|hidden|clip/.test(getComputedStyle(p).overflowX)) return true;
    }
    return false;
}

function medir(): Hallazgo[] {
    const hallazgos: Hallazgo[] = [];
    const limite = document.documentElement.clientWidth;

    for (const el of Array.from(document.querySelectorAll<HTMLElement>('body *'))) {
        const texto = (el.textContent || '').trim();
        if (!texto) continue;

        const s = getComputedStyle(el);
        if (s.display === 'none' || s.visibility === 'hidden') continue;

        const caja = el.getBoundingClientRect();
        if (caja.width === 0) continue;

        const comun = {
            texto: texto.slice(0, 60),
            etiqueta: el.tagName.toLowerCase(),
            clases: (typeof el.className === 'string' ? el.className : '').slice(0, 80),
        };

        // 1. El contenido no cabe y nada lo recorta. Solo en hojas de texto:
        //    en un contenedor el exceso casi siempre es de algún hijo.
        const exceso = el.scrollWidth - el.clientWidth;
        const recorta = s.textOverflow === 'ellipsis' || /auto|scroll/.test(s.overflowX);
        if (el.children.length <= 2 && exceso > 1 && el.clientWidth > 4 && !recorta) {
            el.dataset.rpDesborde = 'caja';
            hallazgos.push({ ...comun, exceso, ancho: el.clientWidth, motivo: 'se sale de su caja' });
            continue;
        }

        // 2. La caja misma rebasa la pantalla. Solo el más externo de la cadena:
        //    si el padre ya se sale, marcar también al hijo es ruido.
        if (caja.right > limite + 1 && !estaContenido(el)) {
            el.dataset.rpDesborde = 'pantalla';
            hallazgos.push({
                ...comun, ancho: Math.round(caja.width),
                exceso: Math.round(caja.right - limite), motivo: 'se sale de la pantalla',
            });
        }
    }
    return hallazgos;
}

function limpiar() {
    document.getElementById(ID_ESTILO)?.remove();
    for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-rp-desborde]'))) {
        delete el.dataset.rpDesborde;
    }
}

export function useDetectorDesborde() {
    useEffect(() => {
        if (!import.meta.env.DEV) return;

        let encendido = false;

        const alternar = () => {
            if (encendido) {
                limpiar();
                encendido = false;
                console.info('[desborde] apagado');
                return;
            }

            const hallazgos = medir();
            const estilo = document.createElement('style');
            estilo.id = ID_ESTILO;
            estilo.textContent = `
                [data-rp-desborde="caja"]     { outline: 2px solid #e11d48 !important; outline-offset: 1px; }
                [data-rp-desborde="pantalla"] { outline: 2px dashed #f97316 !important; outline-offset: 1px; }
            `;
            document.head.appendChild(estilo);
            encendido = true;

            const ancho = document.documentElement.clientWidth;
            if (hallazgos.length === 0) {
                console.info(`[desborde] nada se sale a ${ancho}px de ancho ✓`);
            } else {
                console.warn(`[desborde] ${hallazgos.length} a ${ancho}px de ancho — rojo: se sale de su caja · naranja: se sale de la pantalla`);
                console.table(hallazgos);
            }
        };

        const alTeclado = (e: KeyboardEvent) => {
            if (e.code !== ATAJO || !e.shiftKey || !(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            alternar();
        };

        window.addEventListener('keydown', alTeclado);
        return () => {
            window.removeEventListener('keydown', alTeclado);
            limpiar();
        };
    }, []);
}
